// hooks/useUserAgreements.js
import { useState, useEffect, useMemo, useCallback } from "react";
import apiClient from "utils/apiClient";

const useUserAgreements = () => {
  const [agreements, setAgreements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeAgreement,setActiveAgreement] = useState([]);
  const fetchAgreements = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const url = "profile-service/api/v1/user-agreements";
      const response = await apiClient.get(url);
      const data = response.data;
      if (data && data.content && Array.isArray(data.content)) {
        setAgreements(data.content);
      } else if (Array.isArray(data)) {
        setAgreements(data);
      } else {
        setAgreements([]);
        console.error("API returned unexpected data structure for agreements list:", data);
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message;
      setError(errorMessage);
      console.error("Error fetching user agreements:", errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  const updateAgreementStatus = useCallback(
    async (id, newStatus) => {
      if (!id || !newStatus) {
        throw new Error("Agreement ID and new status are required for status update.");
      }
      try {
        const url = `profile-service/api/v1/user-agreements/${id}/status`; // Assuming a dedicated status endpoint
        
        // Use PATCH method for partial update
        const response = await apiClient.patch(url, { 
            status: newStatus 
        }); 
        
        // Refetch to update the table and reflect any backend changes (e.g., retiring an old ACTIVE agreement)
        fetchAgreements(); 
        return response.data;
      } catch (err) {
        const errorMessage = err.response?.data?.message || err.message;
        setError(errorMessage);
        throw err;
      }
    },
    [fetchAgreements]
  );

  const addAgreement = useCallback(
    async (agreementData) => {
      try {
        const url = "profile-service/api/v1/user-agreements";
        const response = await apiClient.post(url, agreementData);
        fetchAgreements();
        return response.data;
      } catch (err) {
        const errorMessage = err.response?.data?.message || err.message;
        setError(errorMessage);
        throw err;
      }
    },
    [fetchAgreements]
  );

  const updateAgreement = useCallback(
    async (id, agreementData) => {
      if (!id) {
        throw new Error("Agreement ID is required for update.");
      }
      try {
        const url = `profile-service/api/v1/user-agreements/${id}`;
        const response = await apiClient.put(url, agreementData);
        fetchAgreements();
        return response.data;
      } catch (err) {
        const errorMessage = err.response?.data?.message || err.message;
        setError(errorMessage);
        throw err;
      }
    },
    [fetchAgreements]
  );

  const deleteAgreement = useCallback(
    async (id) => {
      if (!id) {
        console.error("Attempted to delete agreement with null ID.");
        return;
      }
      try {
        const url = `profile-service/api/v1/user-agreements/${id}`;
        await apiClient.delete(url);
        // Optimistically update local state
        setAgreements((prev) => prev.filter((a) => a.id !== id));
        fetchAgreements();
        return true;
      } catch (err) {
        const errorMessage = err.response?.data?.message || err.message;
        setError(errorMessage);
        throw err;
      }
    },
    [fetchAgreements]
  );

  const getAgreementById = useCallback(async (id) => {
    if (!id) {
      throw new Error("Agreement ID is required.");
    }
    try {
      const url = `profile-service/api/v1/user-agreements/${id}`;
      const response = await apiClient.get(url);
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message;
      setError(errorMessage);
      console.error(err.response?.data);
      throw err;
    }
  }, []);

  useEffect(() => {
    fetchAgreements();
  }, [fetchAgreements]);

    // Memoized filtered agreements (exclude DELETED)
  const filteredAgreements = useMemo(() => {
    return agreements.filter(a => a.status !== "DELETED");
  }, [agreements]);

  const getActiveAgreement = useCallback(async () => {
  setLoading(true);
  setError(null);

  try {
    const url = "profile-service/api/v1/user-agreements/active";
    const response = await apiClient.get(url);
    const data = response.data;

    if (data?.content && Array.isArray(data.content)) {
      setActiveAgreement(data.content);
    }
    else if (Array.isArray(data)) {
      setActiveAgreement(data);
    }
    else if (data && typeof data === "object") {
      setActiveAgreement([data]); // Normalize into array
    }
    else {
      setActiveAgreement([]);
    }
  } catch (err) {
    const errorMessage = err.response?.data?.message || err.message;
    setError(errorMessage);
  } finally {
    setLoading(false);
  }
}, []);


  return {
    agreements:filteredAgreements,
    loading,
    error,
    refetch: fetchAgreements,
    addAgreement,
    activeAgreement,
    getActiveAgreement,
    updateAgreementStatus,
    updateAgreement,
    deleteAgreement,
    getAgreementById,
  };
};

export default useUserAgreements;
